import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useCategory } from "../context/CategoryContext";
import { showError, showSuccess } from "../utils/toast"; // adjust path

const CompanyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyCategories, setCompanyCategories] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [search, setSearch] = useState("");
  const { selectedCategory } = useCategory();
  const [activeTab, setActiveTab] = useState("All");

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ===== Fetch Policies =====
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const agent = user || JSON.parse(localStorage.getItem("user"));
        if (!agent?.id) return;

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/Insurance-by-Category?insuranceType=${selectedCategory}&agentId=${agent.id}`
        );
        const data = await res.json();

        if (Array.isArray(data)) setPolicies(data);
        else if (Array.isArray(data.policies)) setPolicies(data.policies);
      } catch (err) {
        console.error("Failed to fetch policies:", err);
      }
    };
    fetchCompanies();
  }, [user, selectedCategory]);

  // ===== Group by Company =====
  const companies = useMemo(() => {
    const grouped = {};
    (policies || []).forEach((p) => {
      const company = p.company || "Unknown";
      if (!grouped[company]) {
        grouped[company] = {
          company,
          totalPolicies: 0,
          totalEarnings: 0,
          customers: new Set(),
          lastUpdated: p.createdAt || p.startDate || p.policyDetails?.startDate,
        };
      }
      grouped[company].totalPolicies += 1;
      grouped[company].totalEarnings += p?.policyDetails?.premium || 0;
      if (p.customerPhone) grouped[company].customers.add(p.customerPhone);
    });

    return Object.values(grouped).map((g) => ({
      ...g,
      id: g.company.trim().toLowerCase().replace(/\s+/g, "-"),
      customers: g.customers.size,
    }));
  }, [policies]);

  // ===== Build Dynamic Tabs from policyType field =====
  useEffect(() => {
    if (!policies || policies.length === 0) {
      setCompanyCategories([]);
      return;
    }

    const uniqueTypes = [
      ...new Set(
        policies
          .map((p) => p.policyType)
          .filter((type) => type && type.trim() !== "")
      ),
    ];
    setCompanyCategories(uniqueTypes);
  }, [policies]);

  // ===== Reset Filters =====
  const handleReset = () => {
    setSearch("");
    setActiveTab("All");
  };

  // ===== Filter Logic =====
  const filtered = companies.filter((c) => {
    const matchesSearch = c.company
      .toLowerCase()
      .includes(search.toLowerCase());

    if (activeTab === "All") return matchesSearch;

    const hasType = policies.some(
      (p) =>
        p.company?.toLowerCase() === c.company.toLowerCase() &&
        p.policyType?.toLowerCase() === activeTab.toLowerCase()
    );
    return matchesSearch && hasType;
  });

  // ===== KPI Calculations =====
  const relevantCompanies = 
    activeTab === "All" && search === "" ? companies : filtered;

  const totalCompanies = relevantCompanies.length;

  const totalCustomers = new Set();
  let totalPoliciesCount = 0;
  let totalEarningsCount = 0;

  console.log("Relevant Companies:", relevantCompanies);

  relevantCompanies.forEach((c) => {
    totalPoliciesCount += c.totalPolicies;
    totalEarningsCount += c.totalEarnings;
    (policies || [])
      .filter(
        (p) =>
          p.company?.toLowerCase() === c.company.toLowerCase() &&
          (activeTab === "All" ||
            p.policyType?.toLowerCase() === activeTab.toLowerCase())
      )
      .forEach((p) => {
        if (p.customerPhone) totalCustomers.add(p.customerPhone);
      });
  });

  const premiumCollected = totalEarningsCount;
  const premiumDue = Math.round(premiumCollected * 0.298);

  const fmt = (n) =>
    n?.toLocaleString?.("en-IN", { maximumFractionDigits: 0 }) ?? n;

  const initials = (name = "") =>
    name
      .split(" ")
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  // ===== Render UI =====
  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="main-content flex-grow-1">
        <div className="container py-4 companies-page">
          <div className="page-head d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-1">Companies</h1>
              <p className="mb-0">
                Manage your insurance partner companies and track their performance
              </p>
            </div>
            <button
              type="button"
              className="btn btn-reset d-inline-flex align-items-center gap-2"
              onClick={() => navigate("/agent/company-selection")}
            >
              <i className="bi bi-plus-lg"></i>
              Add Company
            </button>
          </div>

          {/* ===== KPI Cards ===== */}
          <div className="row g-3 mt-2 kpi-row">
            <div className="col-lg-3 col-md-6">
              <div className="kpi-card">
                <div className="kpi-meta">
                  <span className="kpi-label">Total Companies</span>
                  <span className="kpi-icon">
                    <i className="bi bi-building"></i>
                  </span>
                </div>
                <div className="kpi-value">{fmt(totalCompanies)}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="kpi-card">
                <div className="kpi-meta">
                  <span className="kpi-label">Total Customers</span>
                  <span className="kpi-icon">
                    <i className="bi bi-people"></i>
                  </span>
                </div>
                <div className="kpi-value">{fmt(totalCustomers.size)}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="kpi-card">
                <div className="kpi-meta">
                  <span className="kpi-label">Active Policies</span>
                  <span className="kpi-icon">
                    <i className="bi bi-journal-check"></i>
                  </span>
                </div>
                <div className="kpi-value">{fmt(totalPoliciesCount)}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="kpi-card">
                <div className="kpi-meta">
                  <span className="kpi-label">Premium Collected</span>
                  <span className="kpi-icon">
                    <i className="bi bi-graph-up-arrow"></i>
                  </span>
                </div>
                <div className="kpi-value">₹{fmt(premiumCollected)}</div>
              </div>
            </div>
          </div>

          {/* ===== Filters Toolbar ===== */}
          <div className="toolbar row gx-3 gy-3 align-items-end">
            <div className="col-md-12 d-flex gap-2 flex-wrap align-items-center">
              <div className="filter-chip">
                <i className="bi bi-building"></i>
                <select
                  className="chip-select"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                >
                  <option value="">Company Name</option>
                  {companies.map((c) => (
                    <option key={`n-${c.id}`} value={c.company}>
                      {c.company}
                    </option>
                  ))}
                </select>
              </div>

              {companyCategories.length > 0 && (
                <div className="tab-bar-modern d-flex gap-2 flex-wrap justify-content-start">
                  {companyCategories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`btn btn-reset ${
                        activeTab === cat ? "active" : ""
                      } d-flex align-items-center`}
                      onClick={() => setActiveTab(cat)}
                    >
                      <i
                        className={`me-2 ${
                          cat.toLowerCase().includes("motor")
                            ? "bi bi-car-front-fill"
                            : cat.toLowerCase().includes("health")
                            ? "bi bi-heart-fill"
                            : cat.toLowerCase().includes("life")
                            ? "bi bi-shield-fill-check"
                            : cat.toLowerCase().includes("travel")
                            ? "bi bi-airplane-engines"
                            : cat.toLowerCase().includes("home")
                            ? "bi bi-house-fill"
                            : "bi bi-briefcase-fill"
                        }`}
                      ></i>
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              <button className="btn btn-reset" onClick={handleReset}>
                <i className="bi bi-arrow-clockwise"></i> Reset
              </button>
            </div>
          </div>

          {/* ===== Companies Grid ===== */}
          <div className="row g-4 mt-3">
            {filtered.map((c) => (
              <div className="col-xl-4 col-lg-4 col-md-6" key={c.id}>
                <div className="company-card">
                  <div className="card-top">
                    <div className="avatar">{initials(c.company)}</div>
                    <div className="title-wrap">
                      <div className="name">{c.company}</div>
                      <div className="sub">Insurance Partner</div>
                    </div>
                  </div>

                  {/* ===== Updated Dynamic Metrics ===== */}
                  <ul className="metrics">
                    {(() => {
                      const companyPolicies = policies.filter(
                        (p) =>
                          p.company?.toLowerCase() ===
                            c.company.toLowerCase() &&
                          (activeTab === "All" ||
                            p.policyType?.toLowerCase() ===
                              activeTab.toLowerCase())
                      );

                      const companyCustomers = new Set();
                      let companyTotalPolicies = 0;
                      let companyTotalEarnings = 0;

                      companyPolicies.forEach((p) => {
                        companyTotalPolicies += 1;
                        companyTotalEarnings +=
                          p?.policyDetails?.premium || 0;
                        if (p.customerPhone)
                          companyCustomers.add(p.customerPhone);
                      });

                      const shownCustomers =
                        activeTab === "All" && search === ""
                          ? c.customers
                          : companyCustomers.size;
                      const shownPolicies =
                        activeTab === "All" && search === ""
                          ? c.totalPolicies
                          : c.totalPolicies;
                      const shownEarnings =
                        activeTab === "All" && search === ""
                          ? c.totalEarnings
                          : c.totalEarnings;

                      return (
                        <>
                          <li>
                            <span className="m-left">
                              <i className="bi bi-people"></i> Customers
                            </span>
                            <span className="m-right">
                              {fmt(shownCustomers)}
                            </span>
                          </li>
                          <li>
                            <span className="m-left">
                              <i className="bi bi-journal-check"></i> Active
                              Policies
                            </span>
                            <span className="m-right">
                              {fmt(shownPolicies)}
                            </span>
                          </li>
                          <li>
                            <span className="m-left">
                              <i className="bi bi-currency-rupee"></i> Monthly
                              Premium
                            </span>
                            <span className="m-right accent">
                              ₹{fmt(shownEarnings)}
                            </span>
                          </li>
                        </>
                      );
                    })()}
                  </ul>

                  <div className="card-foot">
                    <div className="date">
                      <i className="bi bi-calendar3 me-1" />
                      {(() => {
                        const d = c.lastUpdated ? new Date(c.lastUpdated) : null;
                        return d && !isNaN(d.getTime())
                          ? d.toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "—";
                      })()}
                    </div>
                    <div className="foot-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="View"
                        onClick={() =>
                          navigate(
                            `/agent/company/${encodeURIComponent(
                              c.company
                            )}/${activeTab !== "All"
                              ? encodeURIComponent(activeTab)
                              : ""}`
                          )
                        }
                      >
                        <i className="bi bi-eye" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center text-muted mt-4">
              No companies found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;
